import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, AlertTitle, Box, IconButton, LinearProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import { MonitorButton } from "../../components/MonitorControls";
import DateLabel from "../../components/DateLabel";
import { api, v } from "../../utils/helpers";
import { stageLabel } from "../../utils/stages";

// Zustand als farbiger Text (keine Pills), nur über CSS-Variablen (Vertrag 25.2). Unkritische Regeln sind erlaubt
// (Entscheidung David 24.10.2026) und stehen deshalb in Erfolgsfarbe mit Hinweis, nicht als Warnung.
const STATE_COLOR = { free: "success", harmless: "success", protected: "danger", unverifiable: "warning" };
// Abhilfetexte beginnen mit dem Branchnamen in Backticks. Angezeigt und kopiert wird derselbe Wortlaut ohne Markdown,
// damit der Benutzer sieht, was er einfügt.
const plain = text => String(text || "").replace(/`([^`]*)`/g, "$1");
const remedyText = (remedy, english) => plain((english ? remedy.en : remedy.de) || remedy.de || remedy.en || "");
// Je Branch eine Zeile mit seinen Abhilfen.
const remedyLine = (branch, english) => (branch.remedies || []).map(remedy => remedyText(remedy, english)).join(" ");

/** „Schreibregeln der Stufenbranches“ im CI/CD Monitor (Bereich „Repository“), aus GET /api/cicd/branch-rules (Vertrag 25.2, 25.3). */
export default function BranchRules({ spaceId }) {
  const { t, i18n } = useTranslation(["cicdmonitor", "gemeinsam"]);
  const titleId = useId();
  const english = (i18n.language || "").startsWith("en");
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [copied, setCopied] = useState(null);
  const version = useRef(0);

  const load = useCallback(async (refresh = false) => {
    if (!spaceId) return;
    const current = ++version.current;
    setState(previous => ({ ...previous, loading: true, error: null }));
    try {
      const data = await api(`/api/cicd/branch-rules?spaceId=${encodeURIComponent(spaceId)}${refresh ? "&refresh=1" : ""}`, { cache: "no-store", retry: false });
      if (!data || !Array.isArray(data.branches)) throw new Error(t("schreibregeln.fehler"));
      if (current === version.current) setState({ loading: false, data, error: null });
    } catch (error) {
      if (current === version.current) setState(previous => ({ loading: false, data: previous.data, error }));
    }
  }, [spaceId, t]);
  useEffect(() => { load(false); return () => { version.current += 1; }; }, [load]);

  const writeClipboard = async (text, all) => {
    try { await navigator.clipboard.writeText(text); setCopied({ ok: true, all }); }
    catch { setCopied({ ok: false, all }); }
  };
  const copy = branch => writeClipboard(remedyLine(branch, english), false);

  const { loading, data, error } = state;
  // Ursache als Satz wie in der Transportfreigabe: keine Antwort oder Fehlerantwort mit Servermeldung.
  const reason = !error ? "" : !Number.isInteger(error.status) ? t("schreibregeln.fehlerKeineAntwort")
    : t("schreibregeln.fehlerServer", { message: error.message && !/^HTTP \d{3}$/.test(error.message) ? error.message : `HTTP ${error.status}` });
  // Klartext je Regeltyp; unbekannte Typen zeigen ihre Kennung.
  const ruleLabel = type => (i18n.exists(`cicdmonitor:schreibregeln.regelTypen.${type}`) ? t(`schreibregeln.regelTypen.${type}`) : type);
  const branches = data?.branches || [];
  const withRemedies = branches.filter(branch => branch.remedies?.length);
  // „Alle Abhilfen kopieren“: eine Nachricht an die Administratoren in GitHub, je Branch eine Zeile.
  const copyAll = () => writeClipboard(withRemedies.map(branch => remedyLine(branch, english)).join("\n"), true);

  return <Box component="section" aria-labelledby={titleId}>
    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
      <Typography component="h2" variant="h6" id={titleId} sx={{ color: v("text-1"), fontWeight: 600 }}>{t("schreibregeln.titel")}</Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {withRemedies.length > 0 && <MonitorButton variant="outlined" size="small" startIcon={<ContentCopyRounded/>} onClick={copyAll}>
          {t("schreibregeln.alleKopieren")}</MonitorButton>}
        <MonitorButton variant="outlined" size="small" startIcon={<RefreshRounded/>} disabled={loading} onClick={() => load(true)}>
          {t(loading && data ? "schreibregeln.pruefungLaeuft" : "schreibregeln.erneutPruefen")}
        </MonitorButton>
      </Stack>
    </Stack>
    <Typography variant="body2" sx={{ color: v("text-2"), mb: 1.5 }}>{t("schreibregeln.einleitung")}</Typography>
    {data?.checkedAt && <Typography variant="body2" sx={{ color: v("text-2"), mb: 1.5 }}>
      {t("schreibregeln.geprueftAm")} <DateLabel value={data.checkedAt}/>{data.cached ? ` · ${t("schreibregeln.zwischengespeichert")}` : ""}
    </Typography>}
    {loading && <LinearProgress aria-label={t("schreibregeln.laden")} sx={{ mb: 1.5 }}/>}
    {error && <Alert severity="error" sx={{ mb: 1.5, overflowWrap: "anywhere" }}><AlertTitle>{t("schreibregeln.fehler")}</AlertTitle>{reason}</Alert>}
    {copied && <Alert severity={copied.ok ? "success" : "warning"} role="status" sx={{ mb: 1.5 }} onClose={() => setCopied(null)}
      closeText={t("gemeinsam:aktion.schliessen")}>{t(!copied.ok ? "schreibregeln.kopierFehler" : copied.all ? "schreibregeln.alleKopiert" : "schreibregeln.kopiert")}</Alert>}
    {data && !branches.length && <Box>
      <Typography variant="body2" sx={{ color: v("text-1") }}>{t("schreibregeln.leer")}</Typography>
      <Typography variant="body2" sx={{ color: v("text-2") }}>{t("schreibregeln.leerHinweis")}</Typography>
    </Box>}
    {!!branches.length && <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small" aria-labelledby={titleId}>
        <TableHead>
          <TableRow>
            {["branch", "stufe", "zustand", "regeln", "abhilfe"].map(key => <TableCell key={key} sx={{ color: v("text-2"), fontWeight: 600 }}>
              {t(`schreibregeln.${key}`)}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {branches.map(branch => <TableRow key={branch.name} sx={{ verticalAlign: "top" }}>
            <TableCell sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>
              <Typography component="span" variant="body2" sx={{ fontWeight: 600, display: "block" }}>{branch.name}</Typography>
              {branch.exists === false && <Typography component="span" variant="body2" sx={{ color: v("text-2") }}>{t("schreibregeln.nichtVorhanden")}</Typography>}
            </TableCell>
            <TableCell sx={{ color: v("text-1") }}>
              {(branch.stages || []).map(stageLabel).join(", ")}
              {(branch.roles || []).includes("default") && <Typography component="span" variant="body2" sx={{ color: v("text-2"), display: "block" }}>
                {t("schreibregeln.standardbranch")}</Typography>}
            </TableCell>
            <TableCell>
              <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: v(STATE_COLOR[branch.state] || "text-2") }}>
                {t(`schreibregeln.zustaende.${STATE_COLOR[branch.state] ? branch.state : "unverifiable"}`)}
              </Typography>
              {branch.state === "harmless" && <Typography component="span" variant="body2" sx={{ color: v("text-2"), display: "block" }}>
                {t("schreibregeln.erlaubt")}</Typography>}
              {/* Den Grund nennt die Abhilfe derselben Zeile; fehlt sie, verweist die Zeile auf „Erneut prüfen“. */}
              {(branch.state === "unverifiable" || !STATE_COLOR[branch.state]) && !branch.remedies?.length && <Typography component="span" variant="body2"
                sx={{ color: v("text-2"), display: "block" }}>{t("schreibregeln.nichtPruefbarHinweis")}</Typography>}
            </TableCell>
            <TableCell sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>
              {branch.rules?.length ? <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {branch.rules.map((rule, index) => <li key={`${rule.type}:${rule.rulesetId ?? index}`}>
                  {ruleLabel(rule.type)} <Typography component="span" variant="body2" sx={{ color: v(rule.class === "blocking" ? "danger" : "text-2") }}>
                    ({t(`schreibregeln.klasse.${rule.class === "blocking" ? "blocking" : "harmless"}`)})</Typography>
                  {ruleLabel(rule.type) !== rule.type && <Typography component="span" variant="body2" sx={{ color: v("text-3"), display: "block" }}>{rule.type}</Typography>}
                </li>)}
              </Box> : <Typography component="span" variant="body2" sx={{ color: v("text-2") }}>{t("schreibregeln.keineRegeln")}</Typography>}
            </TableCell>
            <TableCell sx={{ color: v("text-1"), overflowWrap: "anywhere", minWidth: 220 }}>
              {branch.remedies?.length ? <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {branch.remedies.map(remedy => <Typography key={remedy.type} variant="body2" sx={{ mb: 0.5 }}>{remedyText(remedy, english)}</Typography>)}
                </Box>
                <Tooltip title={t("schreibregeln.kopieren", { branch: branch.name })}>
                  <IconButton size="small" aria-label={t("schreibregeln.kopieren", { branch: branch.name })} onClick={() => copy(branch)}>
                    <ContentCopyRounded fontSize="small"/>
                  </IconButton>
                </Tooltip>
              </Stack> : <Typography component="span" variant="body2" sx={{ color: v("text-2") }}>{t("schreibregeln.keineAbhilfe")}</Typography>}
            </TableCell>
          </TableRow>)}
        </TableBody>
      </Table>
    </TableContainer>}
  </Box>;
}
